import {Fragment, memo, useRef, useCallback, useEffect} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox.tsx'
import {Text} from '@/components/ui/text/Text'
import {Bell01Icon} from '@/components/ui/icon/Icon'
import {StyledEmptyNotificationWrapper} from './style'
import {useTranslation} from '@/translations/i18n'
import {UserNotification} from '@/features/notifications/types'
import {NotificationItem} from '@/features/notifications/components/notification-item/NotificationItem'
import {NotificationSkeleton} from '../notification-item/NotificationSkeleton'
import {Divider} from '@/components/ui/divider/Divider'

interface NotificationsContentProps {
    notifications: UserNotification[]
    isLoading?: boolean
    hasMorePages?: boolean
    isFetchingNextPage?: boolean
    onLoadMore?: () => void
    onNotificationClick?: () => void
}

const NotificationsContentComponent = ({
    notifications = [],
    isLoading = false,
    hasMorePages = false,
    isFetchingNextPage = false,
    onLoadMore,
    onNotificationClick
}: NotificationsContentProps) => {
    const {t} = useTranslation()
    const hasNotifications = notifications.length > 0
    const endOfListRef = useRef<HTMLDivElement>(null)

    const handleObserver = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries
            if (entry.isIntersecting && hasMorePages && !isLoading && !isFetchingNextPage && onLoadMore) {
                onLoadMore()
            }
        },
        [hasMorePages, isLoading, isFetchingNextPage, onLoadMore]
    )

    useEffect(() => {
        if (!hasMorePages || !endOfListRef.current) return

        const observer = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '0px',
            threshold: 0.5
        })

        if (endOfListRef.current) {
            observer.observe(endOfListRef.current)
        }

        return () => {
            if (endOfListRef.current) {
                observer.unobserve(endOfListRef.current)
            }
        }
    }, [handleObserver, hasMorePages, notifications.length])

    if (isLoading && !notifications.length) {
        return <NotificationSkeleton />
    }

    if (!hasNotifications) {
        return (
            <StyledEmptyNotificationWrapper gap={2} direction="column" align="center" justify="center">
                <Bell01Icon size={18} />
                <Text size="textSm" palette="neutral" shade="500">
                    {t('notifications:no_notifications')}
                </Text>
            </StyledEmptyNotificationWrapper>
        )
    }

    return (
        <Flexbox direction="column">
            {notifications.map(notification => (
                <Fragment key={notification.id}>
                    <NotificationItem
                        notification={notification}
                        onClick={onNotificationClick ? () => onNotificationClick() : undefined}
                    />
                    <Divider direction="horizontal" />
                </Fragment>
            ))}

            {(hasMorePages || isFetchingNextPage) && (
                <div ref={endOfListRef} style={{height: '20px', padding: '10px 0'}}>
                    {isFetchingNextPage && (
                        <Flexbox justify="center" align="center">
                            <NotificationSkeleton />
                        </Flexbox>
                    )}
                </div>
            )}
        </Flexbox>
    )
}

// Memoize the component to prevent unnecessary re-renders
export const NotificationsContent = memo(NotificationsContentComponent)
